import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageModal from '../common/PageModal'
import SearchWithFilter from '../common/SearchWithFilter'
import LoadingSpinner from '../common/LoadingSpinner'
import EmptyPageResults from '../common/EmptyPageResults'
import SimpleButton from '../common/SimpleButton'
import Dropdown from '../common/Dropdown'
import { ArrowBackThickIcon, ExpandLessIcon, ExpandMoreIcon } from '../../icons'
import { getImageByModel } from '../../utils/functions'
import { trackEvent, gaActions, gaCategories } from '../../utils/ga4'
import { keyboardEvents } from '../../utils/keyboardEvents'
import { no_image_200x200 } from '../../images'
import './spareparts-details-modal.scss'

const SparepartsDetailsModal = (props) => {
  const { t } = useTranslation()
  const {
    selectedProductType,
    selectedModel,
    spareParts,
    isFirstSparePartsLoad,
    totalResults,
    filterQuery,
    expandedSparePart,
    canLoadMore,
    showSpinner,
    modelVariants,
    selectedModelVariant,
    onSelectModel,
    onSelectModelVariant,
    onSearchSpareParts,
    onLoadMore,
    onExpandSparePart,
    onRequestReview,
    onRequestMissingItem,
    onOpenImage,
    closeModal,
  } = props
  const [models, setModels] = useState([])

  useEffect(() => {
    if (selectedProductType) {
      setModels(selectedProductType.models)
    }
  }, [selectedProductType])

  const sparePartsHeaderContent = () => {
    return (
      <div className="spareparts-header" onClick={() => handleCloseModal()}>
        <div className="back-button">
          <div className="back-icon">
            <ArrowBackThickIcon />
          </div>
        </div>
        <p className="title">{t('back')}</p>
      </div>
    )
  }

  const selectModel = (modelName) => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.sparepartsPage,
      label: `Spareparts Page: Mobile Model Selected - ${modelName}`,
    })

    onSelectModel(modelName)
  }

  const handleCloseModal = () => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.sparepartsPage,
      label: 'Spareparts Page: Mobile Modal Closed',
    })

    if (closeModal) {
      closeModal()
    }
  }

  return (
    <div className="spareparts-details-modal-wrapper">
      <PageModal toggle onToggle={() => handleCloseModal()} headerContent={sparePartsHeaderContent()}>
        <div className="spareparts-details-modal-content">
          <div className="product-type-card">
            <div className="image-wrapper d-flex flex-align-center">
              <img src={getImageByModel(selectedProductType.models?.length > 0 ? selectedProductType.models[0].name : '')} className="img" alt="product" />
            </div>
            <p className="product-type-name">{selectedProductType.name}</p>
          </div>
          <p className="model-select-title">{t('product')}</p>
          <Dropdown
            className="dropdown-input model-dropdown"
            placeholder={t('spareparts.no-option-selected')}
            options={models?.map((model) => ({ key: model.name, value: model.name }))}
            initialSelectedKey={selectedModel?.name}
            onChange={(model) => selectModel(model.key)}
            withFiltering
          />
          <p className="model-select-title">{t('model')}</p>
          <Dropdown
            className="dropdown-input"
            placeholder={t('spareparts.no-option-selected')}
            options={
              modelVariants?.map((variant) => ({
                key: variant.id.toString(),
                value: variant.productModelName,
              })) || []
            }
            initialSelectedKey={selectedModelVariant}
            onChange={(variant) => onSelectModelVariant(variant.key)}
            isDisabled={!selectedModel}
            withFiltering
          />
          {selectedModel && (
            <div className="table-wrapper">
              <div className="product-header-wrapper">
                <div className="image-wrapper d-flex flex-align-center">
                  <img src={getImageByModel(selectedModel.name)} className="img" alt={selectedModel.name} />
                </div>
                <div className="product-name-wrapper">
                  <div className="product-name">{selectedModel.name}</div>
                  <div className="product-quantity">{!showSpinner && `${totalResults} ${t('items').toLowerCase()}`}</div>
                </div>
              </div>
              <SearchWithFilter searchText={filterQuery} onSearch={(e) => onSearchSpareParts(e)} />
              <SimpleButton className="request-missing-item-button-mobile simple-button submit-button" onClick={onRequestMissingItem}>
                {t('spareparts.request-missing-sparepart')}
              </SimpleButton>
              {spareParts?.length > 0 &&
                spareParts.map((item, index) => (
                  <div key={index} className="spare-parts-card">
                    <div className="card-item">
                      <>
                        <div className="card-item-title">{t('image')}</div>
                        <div className="card-item-body">
                          <div className="image-wrapper">
                            <img
                              className="clickable-image"
                              src={item.src || no_image_200x200}
                              alt="sparepart"
                              role="button"
                              tabIndex={0}
                              onClick={() => onOpenImage?.(item)}
                              onKeyDown={(e) => e.key === keyboardEvents.ENTER && onOpenImage?.(item)}
                              onError={(e) => {
                                e.currentTarget.onerror = null
                                e.currentTarget.src = no_image_200x200
                              }}
                            />
                          </div>
                        </div>
                      </>
                    </div>
                    <div className="card-item">
                      <div className="card-item-title">{t('spareparts.part-no')}</div>
                      <div className="card-item-body">{item.item_id}</div>
                    </div>
                    <div className="card-item">
                      <div className="card-item-title">{t('description')}</div>
                      <div className="card-item-body">{item.description}</div>
                    </div>
                    <div
                      className="card-item"
                      onClick={() =>
                        item.components?.length > 0 && onExpandSparePart(item.components.length, expandedSparePart === item.item_id ? null : item.item_id)
                      }
                    >
                      <div className="card-item-title">{t('spareparts.components')}</div>
                      <div className="card-item-body">
                        {item.components?.length || 0}
                        {item.components?.length > 0 && <>{expandedSparePart === item.item_id ? <ExpandLessIcon /> : <ExpandMoreIcon />}</>}
                      </div>
                    </div>
                    {expandedSparePart === item.item_id && (
                      <div className="card-item">
                        <div className="card-item-title"></div>
                        <div className="card-item-body">
                          <div className="components-list-wrapper">
                            <ul className="components-list">
                              {item.components.map((component, index) => (
                                <li className="component-wrapper" key={index}>
                                  <div className="component-description">{component.description}</div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="card-item">
                      <SimpleButton className="request-review-button-mobile" onClick={() => onRequestReview(item)}>
                        {t('dealer-portal.request-review')}
                      </SimpleButton>
                    </div>
                  </div>
                ))}
              {spareParts?.length === 0 && !showSpinner && !isFirstSparePartsLoad && (
                <EmptyPageResults className="fit-within-page" title={t('spareparts.no-spareparts')} subtitle={t('cannot_find_matching_search')} />
              )}
              {canLoadMore && (
                <div className="buttons-wrapper">
                  <SimpleButton className="load-more-button" onClick={() => onLoadMore()}>
                    {t('load_more')}
                  </SimpleButton>
                </div>
              )}
            </div>
          )}
          {!selectedModel && <EmptyPageResults className="fit-within-page" title={t('spareparts.no-model-selected')} subtitle={t('spareparts.select-model')} />}
        </div>
      </PageModal>
      {showSpinner && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

export default SparepartsDetailsModal

